import { ApolloLink, Observable, FetchResult, Operation } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";

interface RestDirective {
  path: string;
  method?: string;
  type?: string;
  bodyKey?: string;
}

/**
 * Custom REST link compatible with Apollo Client v4.
 * Uses composition instead of inheritance to avoid ES5/ES6 class issues.
 * Returns null for missing fields to prevent Apollo cache errors.
 */
export const CustomRestLink = (options: { uri: string }): ApolloLink => {
  const uri = options.uri;

  const extractRestDirective = (definition: any): RestDirective | null => {
    if (definition.kind !== "OperationDefinition") {
      return null;
    }

    const selections = definition.selectionSet?.selections || [];
    for (const selection of selections) {
      if (selection.directives) {
        for (const directive of selection.directives) {
          if (directive.name.value === "rest") {
            const args = directive.arguments || [];
            const restConfig: RestDirective = { path: "" };

            args.forEach((arg: any) => {
              const name = arg.name.value;
              const value = arg.value.value;
              if (name === "path") restConfig.path = value;
              if (name === "method") restConfig.method = value;
              if (name === "type") restConfig.type = value;
              if (name === "bodyKey") restConfig.bodyKey = value;
            });

            return restConfig;
          }
        }
      }
    }

    return null;
  };

  /**
   * Wraps data with Proxy to return null for missing fields (lazy evaluation).
   */
  const wrapWithNullProxy = (data: any): any => {
    if (data === null || data === undefined) {
      return null;
    }

    if (Array.isArray(data)) {
      return data.map((item) => wrapWithNullProxy(item));
    }

    if (typeof data === "object") {
      return new Proxy(data, {
        get: (target, prop) => {
          if (prop === Symbol.toStringTag) return "Object";
          if (typeof prop === "symbol") return target[prop];

          const value = target[prop];

          // Return null for missing fields instead of undefined
          if (value === undefined) {
            return null;
          }

          // Recursively wrap nested objects/arrays
          if (value !== null && typeof value === "object") {
            return wrapWithNullProxy(value);
          }

          return value;
        },
        has: () => true,
        ownKeys: (target) => Reflect.ownKeys(target),
        getOwnPropertyDescriptor: (target, prop) => {
          const desc = Object.getOwnPropertyDescriptor(target, prop);
          if (desc) return desc;
          return { configurable: true, enumerable: true, value: null };
        },
      });
    }

    return data;
  };

  /**
   * Interpolates path parameters like {args.bookingId} with actual values from variables.
   */
  const interpolatePath = (path: string, variables: Record<string, any>): string => {
    return path.replace(/\{args\.(\w+)\}/g, (_, key) => {
      const value = variables[key];
      return value !== undefined && value !== null ? encodeURIComponent(String(value)) : "";
    });
  };

  const makeRestRequest = async (
    operation: Operation,
    restDirective: RestDirective,
  ): Promise<FetchResult> => {
    const { variables } = operation;
    const method = restDirective.method?.toUpperCase() || "GET";
    const interpolatedPath = interpolatePath(restDirective.path, variables || {});
    const url = `${uri}${interpolatedPath}`;

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(operation.getContext().headers || {}),
    };

    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    if (method !== "GET" && method !== "HEAD") {
      const bodyKey = restDirective.bodyKey || "body";
      const bodyData = variables[bodyKey];
      if (bodyData) {
        fetchOptions.body = JSON.stringify(bodyData);
      }
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response
      .text()
      .then((t) => (t ? JSON.parse(t) : null))
      .catch(() => null);

    const definition: any = getMainDefinition(operation.query);
    const operationName = definition.selectionSet?.selections?.[0]?.name?.value;

    // Wrap data with proxy to handle missing fields
    const normalizedData = wrapWithNullProxy(data);

    return {
      data: operationName ? { [operationName]: normalizedData } : normalizedData,
    };
  };

  // Use ApolloLink constructor function instead of class inheritance
  return new ApolloLink((operation: Operation, forward) => {
    const definition = getMainDefinition(operation.query);
    const restDirective = extractRestDirective(definition);

    if (!restDirective) {
      return forward(operation);
    }

    return new Observable((observer) => {
      makeRestRequest(operation, restDirective)
        .then((result) => {
          observer.next(result);
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  });
};
